import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LanguageProvider } from '@/context/LanguageContext';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Chatbot from '@/components/Chatbot';
import Home from '@/pages/Home';
import Embassy from '@/pages/Embassy';
import DiplomaticCorps from '@/pages/DiplomaticCorps';
import About from '@/pages/About';
import Services from '@/pages/Services';
import ConsularServices from '@/pages/ConsularServices';
import Appointment from '@/pages/Appointment';
import ConsularCard from '@/pages/ConsularCard';
import AngolaSpain from '@/pages/AngolaSpain';
import InvestInAngola from '@/pages/InvestInAngola';
import BusinessOpportunities from '@/pages/BusinessOpportunities';
import BilateralRelations from '@/pages/BilateralRelations';
import NewsEvents from '@/pages/NewsEvents';
import News from '@/pages/News';
import NewsDetail from '@/pages/NewsDetail';
import Events from '@/pages/Events';
import FAQ from '@/pages/FAQ';
import Contact from '@/pages/Contact';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Payments from '@/pages/Payments';
import UserDashboard from '@/pages/UserDashboard';
import UserProfile from '@/pages/UserProfile';
import SalvoConduto from '@/pages/SalvoConduto';
import DocumentsPage from '@/pages/DocumentsPage';
import BackofficeLayout from '@/backoffice/Layout';
import Dashboard from '@/backoffice/Dashboard';
import Articles from '@/backoffice/Articles';
import ArticleEditor from '@/backoffice/ArticleEditor';
import Users from '@/backoffice/Users';
import AdminPayments from '@/backoffice/AdminPayments';
import Categories from '@/backoffice/Categories';
import AdminAppointments from '@/backoffice/AdminAppointments';
import AdminConsularCards from '@/backoffice/AdminConsularCards';
import FinancialDashboard from '@/backoffice/FinancialDashboard';
import ConsularAgentDashboard from '@/backoffice/ConsularAgentDashboard';
import ConsulDashboard from '@/backoffice/ConsulDashboard';
import ArticleReview from '@/backoffice/ArticleReview';
import NewsletterManagement from '@/backoffice/NewsletterManagement';
import SMSManagement from '@/backoffice/SMSManagement';
import SCondutManagement from '@/backoffice/SCondutManagement';

const PublicLayout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
    <Chatbot />
  </div>
);

const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles.length > 0 && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public Pages - New Structure */}
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/embaixada" element={<PublicLayout><Embassy /></PublicLayout>} />
      <Route path="/corpo-diplomatico" element={<PublicLayout><DiplomaticCorps /></PublicLayout>} />
      <Route path="/sobre" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/servicos-consulares" element={<PublicLayout><ConsularServices /></PublicLayout>} />
      <Route path="/servicos" element={<PublicLayout><Services /></PublicLayout>} />
      <Route path="/angola-espanha" element={<PublicLayout><AngolaSpain /></PublicLayout>} />
      <Route path="/investir-em-angola" element={<PublicLayout><InvestInAngola /></PublicLayout>} />
      <Route path="/oportunidades" element={<PublicLayout><BusinessOpportunities /></PublicLayout>} />
      <Route path="/relacoes-bilaterais" element={<PublicLayout><BilateralRelations /></PublicLayout>} />
      <Route path="/noticias-eventos" element={<PublicLayout><NewsEvents /></PublicLayout>} />
      <Route path="/noticias" element={<PublicLayout><News /></PublicLayout>} />
      <Route path="/noticias/:id" element={<PublicLayout><NewsDetail /></PublicLayout>} />
      <Route path="/eventos" element={<PublicLayout><Events /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
      <Route path="/contactos" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/documentos" element={<PublicLayout><DocumentsPage /></PublicLayout>} />
      <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
      <Route path="/registar" element={<PublicLayout><Register /></PublicLayout>} />
      <Route path="/pagamentos" element={<PublicLayout><Payments /></PublicLayout>} />
      <Route path="/agendamento" element={<PublicLayout><Appointment /></PublicLayout>} />
      <Route path="/cartao-consular" element={<PublicLayout><ConsularCard /></PublicLayout>} />
      <Route path="/salvo-conduto" element={<PublicLayout><SalvoConduto /></PublicLayout>} />
      
      {/* User Dashboard */}
      <Route path="/Users" element={
        <ProtectedRoute>
          <UserDashboard />
        </ProtectedRoute>
      } />
      
      {/* User Profile - Minha Conta */}
      <Route path="/minha-conta" element={
        <ProtectedRoute roles={['user', 'admin', 'editor', 'publisher', 'consular_agent', 'financial', 'auditor']}>
          <PublicLayout><UserProfile /></PublicLayout>
        </ProtectedRoute>
      } />
      
      {/* Backoffice */}
      <Route path="/admin" element={
        <ProtectedRoute roles={['admin', 'editor', 'publisher', 'consul', 'consular_agent', 'financial', 'auditor']}>
          <BackofficeLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={
          <ProtectedRoute roles={['admin', 'publisher']}><Dashboard /></ProtectedRoute>
        } />
        <Route path="artigos" element={
          <ProtectedRoute roles={['admin', 'publisher']}><Articles /></ProtectedRoute>
        } />
        <Route path="artigos/novo" element={
          <ProtectedRoute roles={['admin', 'publisher']}><ArticleEditor /></ProtectedRoute>
        } />
        <Route path="artigos/:id/editar" element={
          <ProtectedRoute roles={['admin', 'publisher']}><ArticleEditor /></ProtectedRoute>
        } />
        <Route path="artigos/revisao" element={
          <ProtectedRoute roles={['admin', 'publisher']}><ArticleReview /></ProtectedRoute>
        } />
        <Route path="utilizadores" element={
          <ProtectedRoute roles={['admin']}><Users /></ProtectedRoute>
        } />
        <Route path="pagamentos" element={
          <ProtectedRoute roles={['admin', 'financial']}><AdminPayments /></ProtectedRoute>
        } />
        <Route path="categorias" element={
          <ProtectedRoute roles={['admin', 'publisher']}><Categories /></ProtectedRoute>
        } />
        <Route path="agendamentos" element={
          <ProtectedRoute roles={['admin', 'consul', 'consular_agent']}><AdminAppointments /></ProtectedRoute>
        } />
        <Route path="CardC" element={
          <ProtectedRoute roles={['admin', 'consul', 'consular_agent']}><AdminConsularCards /></ProtectedRoute>
        } />
        <Route path="SCondut" element={
          <ProtectedRoute roles={['admin', 'consul', 'consular_agent', 'auditor']}><SCondutManagement /></ProtectedRoute>
        } />
        <Route path="financeiro" element={
          <ProtectedRoute roles={['admin', 'financial']}><FinancialDashboard /></ProtectedRoute>
        } />
        <Route path="aconsular" element={
          <ProtectedRoute roles={['admin', 'consul', 'consular_agent']}><ConsularAgentDashboard /></ProtectedRoute>
        } />
        <Route path="consul" element={
          <ProtectedRoute roles={['admin', 'consul']}><ConsulDashboard /></ProtectedRoute>
        } />
        <Route path="newsletter" element={
          <ProtectedRoute roles={['admin', 'publisher']}><NewsletterManagement /></ProtectedRoute>
        } />
        <Route path="sms" element={
          <ProtectedRoute roles={['admin']}><SMSManagement /></ProtectedRoute>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
